import {
  createContext,
  type ComponentType,
  type ReactNode,
  useEffect,
  useCallback,
  use,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";

import { cn } from "@repo/cn";
import type { SVGIconProps } from "@repo/icons/interface";
import { CpsInput } from "corpus";

type CardSelectMode = "single" | "multi";

interface CardSelectContextProps {
  name: string;
  mode: CardSelectMode;
  selectedValues: string[];
  toggle: (value: string) => void;
}

const CardSelectContext = createContext<CardSelectContextProps | undefined>(undefined);

const useCardSelect = (): CardSelectContextProps => {
  const context = use(CardSelectContext);
  if (!context) {
    throw new Error("CardSelectItem must be used within a CardSelectRoot");
  }
  return context;
};

interface SingleModeProps {
  mode?: "single";
  defaultValue?: string;
  onChange?: (value: string) => void;
}

interface MultiModeProps {
  mode: "multi";
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
}

export interface CardSelectTitleProps {
  children: ReactNode;
}

export const CardSelectTitle = ({ children }: CardSelectTitleProps): JSX.Element => {
  return <h1 className="pb-1 text-xl font-semibold text-neutral-700">{children}</h1>;
};

export interface CardSelectDescriptionProps {
  children: ReactNode;
}

export const CardSelectDescription = ({
  children,
}: CardSelectDescriptionProps): JSX.Element => {
  return <p className="pb-3 text-sm text-neutral-500">{children}</p>;
};

export interface CardSelectItemContainerProps {
  children: ReactNode;
  className?: string;
}

export const CardSelectItemContainer = ({
  children,
  className,
}: CardSelectItemContainerProps): JSX.Element => {
  return <div className={cn("flex flex-col gap-3", className)}>{children}</div>;
};

export const CardSelectItemContainerRow = ({
  children,
  className,
}: CardSelectItemContainerProps): JSX.Element => {
  return <div className={cn("grid grid-cols-2 gap-3", className)}>{children}</div>;
};

interface CardSelectRootBaseProps {
  children: ReactNode;
  name: string;
  className?: string;
}

export type CardSelectRootProps = CardSelectRootBaseProps &
  (SingleModeProps | MultiModeProps);

export const CardSelectRoot = ({
  children,
  name,
  mode = "single",
  defaultValue,
  onChange,
  className,
}: CardSelectRootProps): JSX.Element => {
  const normalizedDefault = useMemo(() => {
    if (defaultValue === undefined) return [];
    return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
  }, [defaultValue]);

  const [selectedValues, setSelectedValues] = useState<string[]>(normalizedDefault);

  useEffect(() => {
    setSelectedValues(normalizedDefault);
  }, [normalizedDefault]);

  const toggle = useCallback(
    (value: string): void => {
      if (mode === "single") {
        setSelectedValues([value]);
        (onChange as SingleModeProps["onChange"])?.(value);
        return;
      }

      setSelectedValues((prev) => {
        const exists = prev.includes(value);
        const next = exists ? prev.filter((v) => v !== value) : [...prev, value];
        (onChange as MultiModeProps["onChange"])?.(next);
        return next;
      });
    },
    [mode, onChange],
  );

  const contextValue = useMemo(
    () => ({ name, mode, selectedValues, toggle }),
    [name, mode, selectedValues, toggle],
  );

  return (
    <CardSelectContext value={contextValue}>
      <fieldset className={cn("flex w-full min-w-0 flex-col", className)}>
        {children}
      </fieldset>
    </CardSelectContext>
  );
};

export interface CardSelectItemProps {
  value: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onDisabledClick?: () => void;
}

export interface CardSelectItemLabelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export const CardSelectItemLabel = ({
  value,
  children,
  className,
}: CardSelectItemLabelProps): JSX.Element => {
  const { selectedValues } = useCardSelect();
  const isSelected = selectedValues.includes(value);

  return (
    <span
      className={cn(
        "text-xs text-start",
        isSelected ? "text-primary-600" : "text-neutral-500",
        className,
      )}
    >
      {children}
    </span>
  );
};

const itemShapeClasses =
  "flex w-full items-center justify-center rounded-lg border-2 px-4 py-4 text-center font-medium transition-colors";

export const CardSelectItem = ({
  value,
  children,
  className,
  disabled = false,
  onDisabledClick,
}: CardSelectItemProps): JSX.Element => {
  const { name, mode, selectedValues, toggle } = useCardSelect();
  const isSelected = selectedValues.includes(value);

  if (disabled) {
    return (
      <button
        type="button"
        aria-disabled="true"
        onClick={onDisabledClick}
        className={cn(
          itemShapeClasses,
          "cursor-not-allowed border-neutral-100 bg-neutral-100 text-black opacity-45",
          className,
        )}
      >
        {children}
      </button>
    );
  }

  return (
    <label
      className={cn(
        itemShapeClasses,
        "cursor-pointer",
        {
          "border-primary-700 bg-primary-50 text-primary-700": isSelected,
          "border-neutral-100 bg-neutral-100 text-black": !isSelected,
        },
        className,
      )}
    >
      <input
        type={mode === "single" ? "radio" : "checkbox"}
        name={name}
        value={value}
        checked={isSelected}
        onChange={() => toggle(value)}
        className="sr-only"
      />
      {children}
    </label>
  );
};

export interface CardSelectInputItemProps {
  value: string;
  icon?: ComponentType<SVGIconProps>;
  iconSize?: SVGIconProps["size"];
  children: ReactNode;
  placeholder?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  error?: string;
  className?: string;
}

export const CardSelectInputItem = ({
  value,
  icon: Icon,
  iconSize = 24,
  children,
  placeholder,
  inputValue = "",
  onInputChange,
  error,
  className,
}: CardSelectInputItemProps): JSX.Element => {
  const { name, mode, selectedValues, toggle } = useCardSelect();
  const isSelected = selectedValues.includes(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  useEffect(() => {
    if (isSelected) {
      inputRef.current?.focus();
    }
  }, [isSelected]);

  return (
    <div
      className={cn(
        "flex w-full flex-col rounded-lg border-2 transition-colors",
        {
          "border-primary-700 bg-primary-50": isSelected && !error,
          "border-danger-500 bg-primary-50": isSelected && Boolean(error),
          "border-neutral-50 bg-neutral-50": !isSelected,
        },
        className,
      )}
    >
      <label className="flex w-full cursor-pointer items-center gap-2 px-4 py-4 font-medium">
        <input
          type={mode === "single" ? "radio" : "checkbox"}
          name={name}
          value={value}
          checked={isSelected}
          onChange={() => toggle(value)}
          className="sr-only"
        />
        {Icon ? (
          <span
            className="fill-primary-700 flex shrink-0 items-center"
            aria-hidden="true"
          >
            <Icon size={iconSize} />
          </span>
        ) : null}
        <span className={cn("text-md", isSelected ? "text-primary-700" : "text-black")}>
          {children}
        </span>
      </label>
      {isSelected ? (
        <div className="px-4 pb-4">
          <CpsInput
            ref={inputRef}
            id={inputId}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => onInputChange?.(e.target.value)}
            aria-label={typeof children === "string" ? children : undefined}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${inputId}-error` : undefined}
          />
          {error ? (
            <p
              id={`${inputId}-error`}
              role="alert"
              className="text-danger-600 mt-1 text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
